import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { supabase } from '../config/supabase';
import Layout from '../components/Layout';
import { formatDate } from '../utils/formatters';
import './InviteHistory.css';

const InviteHistory = () => {
  const [links, setLinks] = useState([]);
  const [profilesByUserId, setProfilesByUserId] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [revokingId, setRevokingId] = useState(null);

  useEffect(() => {
    loadLinks();
  }, []);

  const loadLinks = async () => {
    setLoading(true);
    setError('');
    try {
      const { data: linksData, error: linksErr } = await supabase
        .from('client_registration_links')
        .select('id, email, expires_at, used_at, revoked_at, used_by_user_id, created_at, link_type')
        .order('created_at', { ascending: false });

      if (linksErr) {
        setError(linksErr.message || 'Failed to load invites');
        setLoading(false);
        return;
      }

      setLinks(linksData || []);

      const usedIds = (linksData || [])
        .map((l) => l.used_by_user_id)
        .filter(Boolean);
      if (usedIds.length > 0) {
        const { data: profiles } = await supabase
          .from('client_profiles')
          .select('user_id, company_name, email')
          .in('user_id', usedIds);
        const map = {};
        (profiles || []).forEach((p) => {
          map[p.user_id] = p;
        });
        setProfilesByUserId(map);
      }
    } catch (err) {
      setError(err?.message || 'Failed to load invites');
    } finally {
      setLoading(false);
    }
  };

  const handleRevoke = async (linkId) => {
    setRevokingId(linkId);
    try {
      const { error: updateErr } = await supabase
        .from('client_registration_links')
        .update({ revoked_at: new Date().toISOString() })
        .eq('id', linkId);
      if (updateErr) throw updateErr;
      await loadLinks();
    } catch (err) {
      setError(err?.message || 'Failed to revoke');
    } finally {
      setRevokingId(null);
    }
  };

  const getStatus = (link) => {
    if (link.revoked_at) return 'Revoked';
    if (link.used_at) return 'Successful';
    if (new Date(link.expires_at) <= new Date()) return 'Expired';
    return 'Pending';
  };

  const getClientDisplay = (link) => {
    if (!link.used_by_user_id) return '—';
    if (link.link_type === 'stakeholder') return 'Stakeholder account';
    const p = profilesByUserId[link.used_by_user_id];
    if (!p) return '—';
    return p.company_name && p.company_name !== '—' ? p.company_name : p.email || '—';
  };

  const canRevoke = (link) => !link.used_at && !link.revoked_at && new Date(link.expires_at) > new Date();

  if (loading) {
    return (
      <Layout>
        <div className="invite-history">
          <p className="invite-history-loading">Loading...</p>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="invite-history">
        <div className="invite-history-header">
          <Link to="/clients" className="invite-history-back">← Back to Clients</Link>
          <h1>Invite history</h1>
        </div>
        {error && <div className="invite-history-error">{error}</div>}
        {links.length === 0 ? (
          <p className="invite-history-empty">No invite links yet. Generate one from Clients → Add client.</p>
        ) : (
          <div className="invite-history-table-wrap">
            <table className="invite-history-table">
              <thead>
                <tr>
                  <th>Created</th>
                  <th>Type</th>
                  <th>Pre-filled email</th>
                  <th>Expires</th>
                  <th>Status</th>
                  <th>Signed up</th>
                  <th>Action</th>
                </tr>
              </thead>
              <tbody>
                {links.map((link) => (
                  <tr key={link.id}>
                    <td>{formatDate(link.created_at)}</td>
                    <td>{link.link_type === 'stakeholder' ? 'Stakeholder' : 'Client'}</td>
                    <td>{link.email || '—'}</td>
                    <td>{formatDate(link.expires_at)}</td>
                    <td>
                      <span className={`invite-history-status invite-history-status-${getStatus(link).toLowerCase()}`}>
                        {getStatus(link)}
                      </span>
                    </td>
                    <td>{getClientDisplay(link)}</td>
                    <td>
                      {canRevoke(link) ? (
                        <button
                          type="button"
                          className="invite-history-revoke"
                          onClick={() => handleRevoke(link.id)}
                          disabled={revokingId === link.id}
                        >
                          {revokingId === link.id ? 'Revoking…' : 'Revoke'}
                        </button>
                      ) : (
                        '—'
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </Layout>
  );
};

export default InviteHistory;
